import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Layout, Result, Spin, Typography } from "antd";
import dayjs from "dayjs";
import axiosClient from "../api/axiosClient";

const { Content } = Layout;
const { Title, Text } = Typography;

interface ClaimPreview {
  client_name: string;
  service: string;
  slot_start: string;
  slot_end: string;
  expires_at: string;
}

export default function PublicWaitlistClaimPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [gone, setGone] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    axiosClient
      .get(`/public/waitlist-claim/${token}`)
      .then((res) => setPreview(res.data))
      .catch(() => setGone(true))
      .finally(() => setLoading(false));
  }, [token]);

  async function claim() {
    setClaiming(true);
    try {
      const res = await axiosClient.post(`/public/waitlist-claim/${token}`);
      setBookingId(res.data.booking_id);
    } catch {
      setGone(true);
    } finally {
      setClaiming(false);
    }
  }

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Content style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Card style={{ width: "100%", maxWidth: 440 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
          ) : bookingId ? (
            <Result
              status="success"
              title="You're booked!"
              subTitle={preview ? `${preview.service} — ${dayjs(preview.slot_start).format("dddd, MMM D [at] h:mm A")}` : undefined}
            />
          ) : gone || !preview ? (
            <Result
              status="warning"
              title="This link has expired"
              subTitle="Someone else may have already claimed this slot, or the window closed. Check with the salon directly if you'd still like this appointment."
            />
          ) : (
            <div style={{ textAlign: "center" }}>
              <Title level={4} style={{ marginTop: 0 }}>A slot just opened up!</Title>
              <Text type="secondary">Hi {preview.client_name.split(" ")[0]}, here's what's available:</Text>
              <div style={{ margin: "20px 0", padding: 16, background: "#f0f5ff", borderRadius: 10 }}>
                <Text strong style={{ fontSize: 16 }}>{preview.service}</Text>
                <div style={{ color: "#595959", marginTop: 4 }}>
                  {dayjs(preview.slot_start).format("dddd, MMMM D")}
                </div>
                <div style={{ color: "#595959" }}>
                  {dayjs(preview.slot_start).format("h:mm A")} – {dayjs(preview.slot_end).format("h:mm A")}
                </div>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                First come, first served — link expires at {dayjs(preview.expires_at).format("h:mm A")}
              </Text>
              <Button type="primary" block size="large" loading={claiming} onClick={claim} style={{ marginTop: 16 }}>
                Claim This Slot
              </Button>
            </div>
          )}
        </Card>
      </Content>
    </Layout>
  );
}
